import React, { useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { authServiceFactory } from 'src/business/service/auth-service';
import { LoginBanner } from 'src/component/auth/login-banner';
import style from 'src/screen/reset-password/reset-password.module.scss';
import { validateStrongPassword } from 'src/util/password-validation-util';

type FormValues = { password: string; confirmPassword: string };
export const CreatePassword = (): React.ReactElement => {
	const { tempCode } = useParams();
	const navigate = useNavigate();
	const {
		register,
		setValue,
		handleSubmit,
		setError,
		getValues,
		formState: { errors, isValid },
	} = useForm({ mode: 'onChange' });
	const [isLoading, setIsLoading] = useState(false);

	const handleConfirmPassword = async (data: FormValues): Promise<void> => {
		setIsLoading(!isLoading);
		if (data.password !== data.confirmPassword) return handlePasswordMismatch();
		if (!tempCode) return handleCodeMissing();
		try {
			await authServiceFactory().resetPasswordFulfill({ password: data.password, code: tempCode });
			setValue('password', '');
			setValue('confirmPassword', '');
			navigate('/login');
		} catch (e: any) {
			setError(
				'confirmPassword',
				{ type: 'focus', message: e.response.data.data[0].message ?? 'Something went wrong' },
				{ shouldFocus: false },
			);
		} finally {
			setIsLoading(isLoading);
		}
	};
	const handlePasswordMismatch = (): void => {
		setError('confirmPassword', { type: 'focus', message: 'Password mismatch' }, { shouldFocus: true });
	};
	const handleCodeMissing = (): void => {
		setError('confirmPassword', { type: 'focus', message: 'Code does not exist' }, { shouldFocus: false });
	};

	const validatePasswordStrength = async (): Promise<void> => {
		const passwordValue = getValues('password');
		const errMessage = await validateStrongPassword(passwordValue);
		!!errMessage && setError('password', { type: 'focus', message: errMessage }, { shouldFocus: true });
	};

	return (
		<>
			<Container fluid={true}>
				<Row className="signup-background-color">
					<Col
						md={8}
						className="p-0 d-none d-sm-block"
					>
						<LoginBanner />
					</Col>
					<Col
						xs={6}
						md={4}
						className="gap-3 d-flex flex-column align-items-center"
					>
						<div
							className={`${style.form} d-flex align-items-center justify-content-center flex-column pt-5`}
						>
							<h1 className="d-flex align-items-center justify-content-center margin-top pt-5">
								<b>Set Password</b>
							</h1>
							<Form
								onSubmit={handleSubmit(handleConfirmPassword)}
								className="w-100"
							>
								<Form.Group className="form-group pt-5">
									<Form.Label>Password</Form.Label>
									<Form.Control
										type="password"
										className="border-0"
										isInvalid={!!errors.password}
										required={true}
										{...register('password', { required: 'You must enter password' })}
										onBlur={validatePasswordStrength}
									/>
									<Form.Control.Feedback type="invalid">
										{errors.password?.message}
									</Form.Control.Feedback>
								</Form.Group>
								<Form.Group className="form-group pt-2">
									<Form.Label>Confirm Password</Form.Label>
									<Form.Control
										type="password"
										className="border-0"
										isInvalid={!!errors.confirmPassword}
										required={true}
										{...register('confirmPassword', { required: 'You must confirm password' })}
									/>
									<Form.Control.Feedback type="invalid">
										{errors.confirmPassword?.message}
									</Form.Control.Feedback>
								</Form.Group>
								<Form.Group className="d-flex align-items-center justify-content-end">
									<Button
										className={`${style.btn} btn-lg btn-primary continue-button`}
										type="submit"
										disabled={isLoading || !isValid}
									>
										Set Password
									</Button>
								</Form.Group>
							</Form>
						</div>
					</Col>
				</Row>
			</Container>
		</>
	);
};
